import React from 'react';
import {Platform, View} from 'react-native';
import {noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import Gutter from '/component/layout/Gutter';
import BaseScreen from '/component/partial/BaseScreen';
import CabBanner from '/component/partial/CabBanner';
import RankProgressCard from '/component/partial/RankProgressCard';
import Timestamp from '/component/partial/Timestamp';
import {PerformanceTab, Shared} from '/constant/TestIDs';
import {COACH} from '/enum/competency.enum';
import {withTranslation} from '/provider/TranslationProvider';

import EarningsSection from './EarningsSection';
import {usePerformance} from './Performance.hook';
import {styles} from './Performance.style';
import PersonalPerformanceSection from './PersonalPerformanceSection';

import {FEATURE_FLAGS} from '/DevFeatureFlags';

interface PerformanceProps {
  showChatFeature: boolean;
  getPerformanceMetrics: () => void;
  getRankProgression: () => void;
  hasPerformanceMetrics: boolean;
  hasProgressionData: boolean;
  tt: (key: string, defaultValue?: string) => string;
  onNavigationIconPress: () => void;
  onShowReport: () => void;
  onShowInfoPopover: () => void;
  onShowRefreshInfoPopover: () => void;
  currentUserCompetency: string;
  accessibilityElementsHidden: boolean | null;
  onOpenModal: () => void;
}
export function Performance({
  showChatFeature,
  getPerformanceMetrics = noop,
  getRankProgression = noop,
  hasPerformanceMetrics = false,
  hasProgressionData = false,
  tt = withTranslation.ttDefaultProp,
  onNavigationIconPress = noop,
  onShowReport = noop,
  onShowInfoPopover = noop,
  onShowRefreshInfoPopover = noop,
  currentUserCompetency = '',
  accessibilityElementsHidden = null,
  onOpenModal = noop,
}: PerformanceProps) {
  const isWeb = Platform.OS === 'web';

  // Requests data for the Tab
  usePerformance({
    getPerformanceMetrics,
    getRankProgression,
    hasPerformanceMetrics,
    hasProgressionData,
  });

  return (
    <AccessibilityWrapper>
      <BaseScreen
        accessibilityElementsHidden={accessibilityElementsHidden}
        testID={PerformanceTab.performanceScreen}
        showChatFeature={showChatFeature}
        onNavigationIconPress={onNavigationIconPress}
        accessible={true}>
        <Gutter style={styles.header}>
          <View
            accessible={true}
            accessibilityRole="header"
            accessibilityLabel="performance">
            <Text
              testID={Shared.sharedScreenTitle}
              variant="xLarge"
              style={styles.headerText}
              allowFontScaling
              maxFontSizeMultiplier={2}
              accessible={true}
              accessibilityRole="header">
              {tt('header.text', 'Performance')}
            </Text>
          </View>
        </Gutter>
        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <View
            style={
              isWeb
                ? [styles.timestampContainer, {marginBottom: 0}]
                : styles.timestampContainer
            }>
            <Timestamp
              onShowInfoPopover={onShowRefreshInfoPopover}
              infoButtonLabel="Show performance data refresh info"
            />
          </View>
        </View>
        {FEATURE_FLAGS.showCabBonus && currentUserCompetency === COACH && (
          <View style={isWeb ? styles.webSectionSpacing : undefined}>
            <CabBanner />
          </View>
        )}

        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <RankProgressCard
            style={{
              base: isWeb
                ? {...styles.rankProgressCard, marginBottom: 0}
                : styles.rankProgressCard,
            }}
            showDetailedProgress={true}
          />
        </View>
        {hasPerformanceMetrics ? (
          <View style={styles.content}>
            <View style={isWeb ? styles.webSectionSpacing : undefined}>
              <PersonalPerformanceSection
                onShowReport={onShowReport}
                onShowInfoPopover={onShowInfoPopover}
              />
            </View>
            <View style={isWeb ? styles.webSectionSpacing : undefined}>
              <EarningsSection
                onShowReport={onShowReport}
                onShowInfoPopover={onShowInfoPopover}
                onOpenModal={onOpenModal}
              />
            </View>
          </View>
        ) : (
          <LoadingIndicator style={styles.loadingIndicator} />
        )}
      </BaseScreen>
    </AccessibilityWrapper>
  );
}

export default withTranslation('screen.performance')(Performance);
